// Umbrellas, towels, sunbathers, lifeguard tower, volleyball and showers.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, add, canvasTex } from '../lib/factory.js';
import { instanced, instancedRaw } from '../lib/instancing.js';
import { textTex } from '../textures/signs.js';
import { sandH } from './shore.js';
import { colliders } from './eixample.js';
import { streets } from './landmarks.js';
import { mergeAny } from './facade-details.js';
import { makePalm } from './park.js';
import { makePerson, walkers } from '../people/models.js';
import { beachSlope } from '../rules/beach.js';
import { beachLie, swimSpots, volley, waders } from '../people/crowd.js';

export function buildBeachLife() {
  // ---------- beach life: umbrellas, towels, sunbathers, lifeguard tower, volleyball, showers ----------
  {
    const sandY = (z) => 0.3 - THREE.MathUtils.clamp((z - 51) / 14, 0, 1) * 0.3;
    const stripe = canvasTex(64, 8, (c) => {
      for (let i = 0; i < 8; i++) {
        c.fillStyle = i % 2 ? '#ffffff' : '#bdbdbd';
        c.fillRect(i * 8, 0, 8, 8);
      }
    });
    stripe.repeat.set(1, 1);
    const spots = [
      [-33, 55],
      [-27, 58],
      [-19, 54.5],
      [-12, 57.5],
      [-6, 55],
      [-1, 59.5],
      [13, 55.5],
      [18, 58.8],
      [25, 54.8],
      [31, 57.2],
      [37, 55.5],
    ];
    const umb = spots.map(([x, z]) => ({
      x,
      z,
      y: sandY(z),
      t: rr(-0.18, 0.18),
      r: rr(0, PI * 2),
      col: pick([0xd9412c, 0x2f6fd0, 0xf2c014, 0x3f8f4f, 0xe86aa6, 0xf28c28]),
    }));
    // parasols: a striped eight-panel canopy, its ribs, a scalloped valance round the rim and a little finial on top
    const placeU = (o, u) => {
      o.position.set(u.x, u.y, u.z);
      o.rotation.set(u.t, u.r, u.t * 0.5);
    };
    const valTex = canvasTex(256, 32, (c) => {
      for (let i = 0; i < 16; i++) {
        c.fillStyle = i % 2 ? '#ffffff' : '#bdbdbd';
        c.beginPath();
        c.moveTo(i * 16, 0);
        c.lineTo(i * 16 + 16, 0);
        c.lineTo(i * 16 + 16, 18);
        c.arc(i * 16 + 8, 18, 8, 0, PI);
        c.closePath();
        c.fill();
      }
    });
    const umbMesh = (geo, mat) => {
      const im = instancedRaw(geo, mat, umb, placeU, (u) => u.col);
      scene.add(im);
      return im;
    };
    umbMesh(
      new THREE.ConeGeometry(1.15, 0.42, 8, 1, true).translate(0, 2.05, 0),
      M(0xffffff, { map: stripe, side: THREE.DoubleSide, flatShading: true }),
    );
    umbMesh(
      new THREE.CylinderGeometry(1.15, 1.15, 0.16, 8, 1, true).translate(0, 1.76, 0),
      M(0xffffff, { map: valTex, alphaTest: 0.5, side: THREE.DoubleSide, flatShading: true }),
    ).castShadow = false;
    const ribs = instancedRaw(
      mergeGeometries(
        [...Array(8)].map((_, k) => {
          const a = (k * PI) / 4;
          return new THREE.CylinderGeometry(0.008, 0.008, 1.23, 4)
            .rotateZ(PI / 2 - 0.35)
            .rotateY(-a)
            .translate(Math.cos(a) * 0.58, 2.04, Math.sin(a) * 0.58);
        }),
      ),
      P(0xdadada, { roughness: 0.4, metalness: 0.6 }),
      umb,
      placeU,
    );
    ribs.castShadow = false;
    scene.add(ribs);
    umbMesh(
      new THREE.SphereGeometry(0.05, 10, 8).translate(0, 2.3, 0),
      P(0xffffff, { roughness: 0.3 }),
    );
    instanced(
      new THREE.CylinderGeometry(0.025, 0.025, 2.1, 8).translate(0, 1.05, 0),
      P(0xe6e6e6, { roughness: 0.3, metalness: 0.6 }),
      umb,
      (o, u) => {
        o.position.set(u.x, u.y, u.z);
        o.rotation.set(u.t, u.r, u.t * 0.5);
      },
    );
    const inCourt = (t) => t.x > -27 && t.x < -17 && t.z > 54.5 && t.z < 60; // keep the volleyball court clear
    const towels = [];
    umb.forEach((u) => {
      for (const d of [-1, 1])
        if (rnd() < 0.8) {
          const t = {
            x: u.x + d * rr(0.8, 1.4),
            z: u.z + rr(-0.6, 0.6),
            y: u.y + 0.012,
            r: rr(-0.4, 0.4),
          };
          if (!inCourt(t)) towels.push(t);
        }
    });
    const towelTex = canvasTex(32, 64, (c) => {
      c.fillStyle = '#fff';
      c.fillRect(0, 0, 32, 64);
      c.fillStyle = 'rgba(0,0,0,.18)';
      for (let y = 0; y < 64; y += 12) c.fillRect(0, y, 32, 5);
    });
    // towels draped over the sand: a subdivided cloth whose every vertex sits just above the beach at that spot, plus
    // soft wrinkles and a slightly rucked-up end, so none sink into the dunes and each one reads as fabric
    const towelMats = [0x2f6fd0, 0xe8445a, 0xf5c542, 0x3fb7a6, 0xf28c28, 0x8e5bb5].map((c) =>
      M(c, { map: towelTex, flatShading: false, roughness: 0.95 }),
    );
    for (const t of towels) {
      const geo = new THREE.PlaneGeometry(0.65, 1.5, 8, 18).rotateX(-PI / 2),
        p = geo.attributes.position,
        cs = Math.cos(t.r),
        sn = Math.sin(t.r),
        ph = rnd() * 10;
      for (let i = 0; i < p.count; i++) {
        const lx = p.getX(i),
          lz = p.getZ(i),
          wx = t.x + lx * cs + lz * sn,
          wz = t.z - lx * sn + lz * cs;
        // ripples across the cloth, one soft fold running diagonally, and the far end rucked up
        const fold = Math.exp(-(((lz - lx * 0.8 - ((ph % 1) - 0.5) * 0.6) / 0.07) ** 2)) * 0.02;
        const wrinkle =
          (Math.sin(lz * 11 + ph + Math.sin(lx * 7) * 0.8) * 0.5 + 0.5) * 0.012 +
          fold +
          Math.max(0, Math.abs(lz) - 0.66) * 0.2 * (Math.sin(lx * 14 + ph) * 0.5 + 0.5);
        p.setXYZ(i, wx, sandH(wx, wz) + 0.022 + wrinkle, wz);
      }
      geo.computeVertexNormals();
      add(geo, pick(towelMats), 0, 0, 0).castShadow = false;
      t.y = sandH(t.x, t.z) + 0.022; // people and things on the towel sit on this height
    }
    // people sunbathing on most towels (posed later from the character models)
    towels.forEach((t, i) => {
      if (i % 3 !== 2) beachLie.push(t);
    });
    // beach clutter around the towels: cool boxes, beach bags, flip-flops at the foot end, inflatable balls, deck chairs
    const nearT = (t, dx, dz) => ({
      x: t.x + Math.cos(t.r) * dx + Math.sin(t.r) * dz,
      z: t.z - Math.sin(t.r) * dx + Math.cos(t.r) * dz,
      r: t.r + rr(-0.4, 0.4),
    });
    const atS = (o, p) => {
      o.position.set(p.x, sandH(p.x, p.z), p.z);
      o.rotation.y = p.r;
    };
    const coolers = towels.filter((_, i) => i % 4 === 0).map((t) => nearT(t, 0.62, 0.45)),
      bags = towels.filter((_, i) => i % 4 === 1).map((t) => nearT(t, -0.58, -0.35));
    instanced(
      new RoundedBoxGeometry(0.34, 0.22, 0.24, 2, 0.03).translate(0, 0.11, 0),
      M(0xffffff, { flatShading: false }),
      coolers,
      atS,
      [0x2f6fd0, 0xd9412c, 0x3fb7a6],
    );
    instanced(
      new RoundedBoxGeometry(0.36, 0.05, 0.26, 2, 0.02).translate(0, 0.24, 0),
      M(0xf4f2ec, { flatShading: false }),
      coolers,
      atS,
    );
    instanced(
      mergeAny([
        new RoundedBoxGeometry(0.32, 0.22, 0.12, 2, 0.04).translate(0, 0.11, 0),
        new THREE.TorusGeometry(0.08, 0.012, 6, 14, PI).translate(0, 0.22, 0),
      ]),
      M(0xffffff, { flatShading: false, roughness: 0.9 }),
      bags,
      atS,
      [0xe9c46a, 0xf4a261, 0x2a9d8f, 0xe76f51, 0xf2efe6],
    );
    const flops = [];
    towels.forEach((t, i) => {
      if (i % 3 === 0) for (const d of [-0.07, 0.07]) flops.push(nearT(t, d, 0.86));
    });
    instanced(
      new RoundedBoxGeometry(0.075, 0.014, 0.2, 1, 0.006),
      M(0xffffff, { flatShading: false }),
      flops,
      (o, p) => {
        o.position.set(p.x, sandH(p.x, p.z) + 0.03, p.z);
        o.rotation.y = p.r;
      },
      [0x1d2433, 0xd9412c, 0xf2c014, 0x2f6fd0],
    ).castShadow = false;
    const ballTex = canvasTex(128, 64, (c) => {
      ['#d9412c', '#ffffff', '#2f6fd0', '#ffffff', '#f2c014', '#ffffff'].forEach((col, i) => {
        c.fillStyle = col;
        c.fillRect((i * 128) / 6, 0, 128 / 6 + 1, 64);
      });
    });
    instanced(
      new THREE.SphereGeometry(0.15, 18, 12).translate(0, 0.15, 0),
      M(0xffffff, { map: ballTex, flatShading: false, roughness: 0.35 }),
      [
        [-9, 58.8],
        [6.5, 56.2],
        [21.5, 59.5],
        [-30.5, 56],
      ].map(([x, z]) => ({ x, z, r: rr(0, PI) })),
      atS,
    );
    // canvas deck chairs under some parasols: a striped sling on a folding wooden frame
    const chairs = umb
      .filter((_, i) => i % 3 === 1)
      .map((u) => ({
        x: u.x + rr(-0.5, 0.5),
        z: u.z - 1.2,
        r: rr(-0.3, 0.3) + PI,
        col: pick([0xd9412c, 0x2f6fd0, 0x3fb7a6, 0xf2c014]),
      }));
    const sling = instancedRaw(
      new THREE.BoxGeometry(0.5, 0.02, 1.05).rotateX(-0.55).translate(0, 0.42, 0),
      M(0xffffff, { map: stripe, flatShading: false }),
      chairs,
      atS,
      (c) => c.col,
    );
    scene.add(sling);
    instanced(
      mergeGeometries(
        [-1, 1]
          .flatMap((sx) => [
            new THREE.BoxGeometry(0.03, 0.03, 1.12).rotateX(-0.55).translate(sx * 0.26, 0.42, 0),
            new THREE.BoxGeometry(0.03, 0.03, 0.62).rotateX(0.95).translate(sx * 0.26, 0.24, 0.2),
          ])
          .concat([new THREE.BoxGeometry(0.55, 0.03, 0.03).translate(0, 0.14, 0.52)]),
      ),
      M(0xb88a5a, { flatShading: false }),
      chairs,
      atS,
    );
    // people knee-deep at the water's edge, and a volleyball game
    for (const [x, z, r] of [
      [-14, 65.2, 0],
      [-13.2, 65.8, PI + 0.4],
      [3, 66, 0.3],
      [10.5, 65.4, PI],
      [11.2, 66.3, -0.5],
      [24, 65.8, 0],
      [-31, 66.2, 0.5],
      [33, 65.5, PI + 0.2],
    ])
      waders.push({ x, z, y: beachSlope(z), r });
    for (const [x, z, r] of [
      [-23.6, 55.6, 0],
      [-20.4, 55.4, 0],
      [-23.8, 58.6, PI],
      [-20.2, 58.4, PI],
    ])
      volley.push({ x, z, y: beachSlope(z), r });
    // strollers along the water's edge: out on the wet sand, back a little higher up the beach
    {
      const path = [
          [-40, 61.8],
          [40, 61.8],
          [40, 60.3],
          [-40, 60.3],
        ],
        lens = [],
        b = { cx: 0, cz: 40 };
      let total = 0;
      for (let k = 0; k < 4; k++) {
        const [ax, az] = path[k],
          [bx, bz] = path[(k + 1) % 4];
        lens.push(Math.hypot(bx - ax, bz - az));
        total += lens[k];
      }
      for (let i = 0; i < 12; i++) {
        const s0 = rr(0, total),
          v = rr(0.38, 0.58),
          pair = rnd() < 0.4,
          group = [];
        for (let k = 0; k < (pair ? 2 : 1); k++) {
          const { g, legs } = makePerson(false);
          scene.add(g);
          group.push({
            g,
            legs,
            path,
            lens,
            total,
            b,
            s: s0,
            v,
            ph: rr(0, 6),
            lat: 0,
            vv: 0,
            dist: 0,
            off: k * 0.34,
            ground: beachSlope,
            beach: true,
            dog: null,
          });
        }
        if (pair) {
          group[0].mate = group[1];
          group[1].mate = group[0];
        }
        walkers.push(...group);
      }
    }
    // lifeguard tower with the red-cross flag
    {
      const g = new THREE.Group();
      g.position.set(22, sandY(56), 56);
      scene.add(g);
      const wood = M(0xe9e4da, { flatShading: false }),
        red = M(0xd2302a, { flatShading: false });
      for (const [x, z] of [
        [-0.6, -0.6],
        [0.6, -0.6],
        [-0.6, 0.6],
        [0.6, 0.6],
      ])
        add(new THREE.CylinderGeometry(0.06, 0.07, 2.4, 8), wood, x, 1.2, z, g);
      add(new RoundedBoxGeometry(1.7, 0.12, 1.7, 2, 0.03), wood, 0, 2.4, 0, g);
      add(
        new RoundedBoxGeometry(1.5, 1.1, 1.5, 2, 0.05),
        [red, red, wood, wood, P(0x1e2733, { roughness: 0.05 }), red],
        0,
        3.05,
        0,
        g,
      );
      add(new RoundedBoxGeometry(1.9, 0.1, 1.9, 2, 0.03), red, 0, 3.65, 0, g);
      add(new THREE.CylinderGeometry(0.02, 0.02, 1.4, 6), wood, 0.8, 4.3, 0.8, g);
      const flagTex = textTex(64, 40, (c) => {
        c.fillStyle = '#fff';
        c.fillRect(0, 0, 64, 40);
        c.fillStyle = '#d2302a';
        c.fillRect(26, 6, 12, 28);
        c.fillRect(16, 14, 32, 12);
      });
      add(
        new THREE.PlaneGeometry(0.7, 0.45),
        M(0xffffff, { map: flagTex, side: THREE.DoubleSide, flatShading: false }),
        1.15,
        4.75,
        0.8,
        g,
      );
      colliders.push({ type: 'rect', x0: 21, x1: 23, z0: 55, z1: 57 });
    }
    // beach volleyball net
    {
      const netTex = canvasTex(64, 64, (c) => {
        c.strokeStyle = '#f4f4f4';
        c.lineWidth = 2;
        for (let i = 0; i <= 64; i += 8) {
          c.beginPath();
          c.moveTo(i, 0);
          c.lineTo(i, 64);
          c.moveTo(0, i);
          c.lineTo(64, i);
          c.stroke();
        }
      });
      netTex.repeat.set(8, 1);
      const y = sandY(57);
      for (const x of [-25.5, -18.5])
        add(
          new THREE.CylinderGeometry(0.05, 0.05, 2.6, 8),
          M(0xf2f0ea, { flatShading: false }),
          x,
          y + 1.3,
          57,
        );
      add(
        new THREE.PlaneGeometry(7, 0.9),
        M(0xffffff, { map: netTex, transparent: true, alphaTest: 0.4, side: THREE.DoubleSide }),
        -22,
        y + 2.05,
        57,
      ).castShadow = false;
    }
    // pedestrian-crossing signs on the right-hand curb of every approach
    const signTex = textTex(64, 64, (c) => {
      c.fillStyle = '#fff';
      c.fillRect(0, 0, 64, 64);
      c.fillStyle = '#1f5fbf';
      c.fillRect(3, 3, 58, 58);
      c.fillStyle = '#fff';
      c.beginPath();
      c.moveTo(32, 9);
      c.lineTo(56, 52);
      c.lineTo(8, 52);
      c.closePath();
      c.fill();
      c.fillStyle = '#111';
      c.beginPath();
      c.arc(32, 25, 3.5, 0, PI * 2);
      c.fill();
      c.fillRect(30, 29, 4, 11);
      c.fillRect(26, 40, 3, 8);
      c.fillRect(35, 40, 3, 8);
      c.fillStyle = '#111';
      for (let k = 0; k < 4; k++) c.fillRect(14 + k * 10, 48, 6, 2);
    });
    const signs = [];
    for (const ix of streets)
      for (const iz of streets) {
        if (iz + 9 < 46) signs.push({ x: ix + 2.6, z: iz + 9.3, r: 0 });
        if (iz - 9 > -46) signs.push({ x: ix - 2.6, z: iz - 9.3, r: PI });
        if (ix + 9 < 46) signs.push({ x: ix + 9.3, z: iz - 2.6, r: PI / 2 });
        if (ix - 9 > -46) signs.push({ x: ix - 9.3, z: iz + 2.6, r: -PI / 2 });
      }
    instanced(
      new THREE.CylinderGeometry(0.03, 0.03, 2.3, 8).translate(0, 1.15, 0),
      P(0x8f9399, { roughness: 0.4, metalness: 0.7 }),
      signs,
      (o, sg) => o.position.set(sg.x, 0.36, sg.z),
    );
    const sgMat = M(0xd9dde0, { flatShading: false });
    instanced(
      new THREE.BoxGeometry(0.5, 0.5, 0.03),
      [sgMat, sgMat, sgMat, sgMat, M(0xffffff, { map: signTex, flatShading: false }), sgMat],
      signs,
      (o, sg) => {
        o.position.set(sg.x, 2.5, sg.z);
        o.rotation.y = sg.r;
      },
    );
    // buoys marking the swimming area, a few swimmers, and a sand castle
    const buoys = [];
    for (let x = -42; x <= 42; x += 7) buoys.push({ x, z: 74 + Math.sin(x) * 0.5 });
    instanced(
      new THREE.SphereGeometry(0.28, 16, 12),
      P(0xf2c014, { roughness: 0.35 }),
      buoys,
      (o, b) => o.position.set(b.x, -0.3, b.z),
    );
    for (const [x, z] of [
      [-12, 68.5],
      [-10.8, 69.2],
      [8, 69],
      [15, 67.8],
      [27, 70],
      [-30, 68],
      [-4, 71],
      [20, 72],
    ])
      swimSpots.push({ x, z, r: rr(-PI, PI) });
    {
      // a child-sized sand castle on open sand (clear of the shoreline walk and the towels): a moat round a mound, a keep
      // moulded from a bucket (ridged), four turrets with crenellations joined by low walls, shells pressed in, a flag
      const castleTex = canvasTex(128, 64, (c) => {
        const g = c.createLinearGradient(0, 0, 0, 64);
        g.addColorStop(0, '#dcc392');
        g.addColorStop(1, '#b8996a');
        c.fillStyle = g;
        c.fillRect(0, 0, 128, 64);
        c.strokeStyle = 'rgba(120,95,60,.45)';
        c.lineWidth = 1.5;
        for (let y = 8; y < 64; y += 9) {
          c.beginPath();
          c.moveTo(0, y + rr(-1, 1));
          c.lineTo(128, y + rr(-1, 1));
          c.stroke();
        }
        for (let k = 0; k < 260; k++) {
          c.fillStyle = rnd() < 0.5 ? 'rgba(110,85,55,.35)' : 'rgba(245,230,200,.45)';
          c.fillRect(rnd() * 128, rnd() * 64, 1.3, 1.3);
        }
        for (let k = 0; k < 7; k++) {
          c.fillStyle = pick(['#f4ece0', '#e7c9b6', '#cfc4b2']);
          c.beginPath();
          c.ellipse(rnd() * 128, 10 + rnd() * 44, 2.2, 1.6, rnd() * PI, 0, PI * 2);
          c.fill();
        }
      });
      const sandM = M(0xffffff, { map: castleTex, flatShading: false, roughness: 1 }),
        wetM = M(0x8f7550, { flatShading: false, roughness: 0.6 });
      const cx = 6,
        cz = 58.4,
        y = sandH(cx, cz);
      add(
        new THREE.TorusGeometry(0.36, 0.045, 8, 32).rotateX(PI / 2),
        wetM,
        cx,
        y + 0.01,
        cz,
      ).castShadow = false; // moat
      add(new THREE.CylinderGeometry(0.3, 0.34, 0.08, 24), sandM, cx, y + 0.04, cz); // mound
      for (let k = 0; k < 4; k++) {
        const a = (k * PI) / 2 + PI / 4,
          x = cx + Math.cos(a) * 0.22,
          z = cz + Math.sin(a) * 0.22;
        add(new THREE.CylinderGeometry(0.055, 0.065, 0.14, 12), sandM, x, y + 0.15, z); // turret
        for (let m = 0; m < 5; m++) {
          const b = (m / 5) * PI * 2;
          add(
            new THREE.BoxGeometry(0.022, 0.03, 0.022),
            sandM,
            x + Math.cos(b) * 0.048,
            y + 0.235,
            z + Math.sin(b) * 0.048,
          );
        }
        const a2 = a + PI / 2,
          mx = cx + (Math.cos(a) + Math.cos(a2)) * 0.11,
          mz = cz + (Math.sin(a) + Math.sin(a2)) * 0.11;
        add(new THREE.BoxGeometry(0.24, 0.07, 0.04), sandM, mx, y + 0.115, mz).rotation.y =
          -(a + PI / 4) + PI / 2; // wall
      }
      add(new THREE.CylinderGeometry(0.09, 0.12, 0.2, 16), sandM, cx, y + 0.18, cz); // keep
      for (let m = 0; m < 7; m++) {
        const b = (m / 7) * PI * 2;
        add(
          new THREE.BoxGeometry(0.03, 0.035, 0.03),
          sandM,
          cx + Math.cos(b) * 0.08,
          y + 0.295,
          cz + Math.sin(b) * 0.08,
        );
      }
      add(new THREE.CylinderGeometry(0.004, 0.004, 0.2, 4), M(0x8a6a4a), cx, y + 0.38, cz);
      add(
        new THREE.PlaneGeometry(0.08, 0.05).translate(0.04, 0, 0),
        M(0xd9412c, { side: THREE.DoubleSide }),
        cx,
        y + 0.45,
        cz,
      );
      for (let k = 0; k < 6; k++) {
        const a = rr(0, PI * 2);
        add(
          new THREE.SphereGeometry(0.012, 8, 6).scale(1, 0.5, 1),
          M(pick([0xf4ece0, 0xe7c9b6])),
          cx + Math.cos(a) * 0.31,
          y + 0.07,
          cz + Math.sin(a) * 0.31,
        );
      }
      // bucket and spade beside it
      add(
        new THREE.CylinderGeometry(0.07, 0.055, 0.1, 12),
        M(0x2f6fd0, { flatShading: false }),
        cx + 0.55,
        y + 0.05,
        cz + 0.15,
      );
      add(
        new THREE.BoxGeometry(0.025, 0.15, 0.07).rotateZ(0.6),
        M(0xf2c014, { flatShading: false }),
        cx + 0.65,
        y + 0.06,
        cz - 0.05,
      );
      window.CASTLE = { x: cx, z: cz };
    }
    for (let x = -37; x <= 42; x += 9.5)
      if (Math.abs(x + 24) > 3 && Math.abs(x + 15) > 2 && Math.abs(x - 28) > 2)
        makePalm({ x, z: 49.7 });
    // beach showers along the promenade edge
    for (const x of [-15, -8, 28]) {
      add(
        new THREE.CylinderGeometry(0.05, 0.06, 2.4, 10),
        P(0xb8bcc2, { roughness: 0.3, metalness: 0.8 }),
        x,
        1.5,
        51.6,
      );
      add(
        new THREE.CylinderGeometry(0.14, 0.1, 0.06, 14),
        P(0xb8bcc2, { roughness: 0.3, metalness: 0.8 }),
        x,
        2.7,
        51.75,
      );
      add(new THREE.BoxGeometry(0.9, 0.06, 0.9), M(0x8a6a4a), x, 0.32, 51.6);
    }
  }
}
